// react
import React, { useId } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

// material ui
import {
  Box,
  Card,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Gauge, gaugeClasses } from '@mui/x-charts';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// feature-components
import { isSummaryOutOfBounds } from '@/features/energyxplorer/components/timestamps-summary/timestampsSummaryEvaluation';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';
import { formatNumber } from '@/utils/formatters';

// relative
import DistributionChart from './highcharts/DistributionChart';

const EnergyCoverageCard = ({
  title,
  description,
  energyOverviewDataResult,
}) => {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const { energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  // const timeRange = useSelector(selectTimeRange);
  const { timeRange } = useUrlDateRange();
  const shouldRenderHashedGauge = isSummaryOutOfBounds(
    energyOverviewDataResult.TimestampsSummary,
    timeRange,
  );

  const [openDistributionChart, setOpenDistributionChart] =
    React.useState(false);
  const handleOpenDistributionChart = () => {
    setOpenDistributionChart(true);
  };

  const analyzed =
    energyOverviewDataResult?.EnergyDataSummary?.MeasuredEnergyPercentage;

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={title}
          avatar={
            <div>
              {shouldRenderHashedGauge ? (
                <HashedGauge
                  analyzed={analyzed} // your % value
                  gaugeColor={(theme) =>
                    theme.palette[selectedEnergyGroup?.type?.toLowerCase()]
                      ?.main || '#52b202'
                  }
                  width={130}
                  height={100}
                />
              ) : (
                <Gauge
                  width={130}
                  height={100}
                  value={analyzed ? analyzed : 0}
                  cornerRadius={'40%'}
                  startAngle={-110}
                  endAngle={110}
                  text={analyzed ? `${formatNumber(analyzed)}%` : '0%'}
                  sx={(theme) => {
                    const gaugeColor =
                      theme.palette[selectedEnergyGroup?.type?.toLowerCase()]
                        ?.main || '#52b202';
                    return {
                      [`& .${gaugeClasses.valueText}`]: { fontSize: 18 },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: gaugeColor || '#52b202',
                        opacity: 0.8,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled || '#000000',
                        opacity: 0.5,
                      },
                      margin: '0 auto',
                    };
                  }}
                />
              )}
            </div>
          }
          subheader={
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                {description}
              </Typography>
            </Box>
          }
          action={
            <IconButton onClick={handleOpenDistributionChart} size="small">
              <OpenInFullRoundedIcon />
            </IconButton>
          }
          slotProps={{
            title: { variant: 'h6', gutterBottom: true }
          }}
        />
      </Card>
      <Dialog
        open={openDistributionChart}
        onClose={() => setOpenDistributionChart(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{t('consumption_group_pie_chart.title')}</DialogTitle>
        <DialogContent dividers>
          <DistributionChart
            data={energyOverviewDataResult?.EnergyDistributionData || []}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnergyCoverageCard;

function HashedGauge({
  analyzed = 0,
  gaugeColor = '#52b202',
  width = 130,
  height = 100,
}) {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const theme = useTheme();
  const patternId = useId().replace(/:/g, '_');
  const resolvedColor =
    typeof gaugeColor === 'function' ? gaugeColor(theme) : gaugeColor;

  return (
    <Tooltip title={t('energy_coverage_card.warning_tooltip')}>
      <Box position="relative">
        {/* Define the pattern BEFORE the Gauge so it's available when styles apply */}
        <svg width="0" height="0" aria-hidden focusable="false">
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width="12"
              height="12"
              patternTransform="rotate(45)"
            >
              {/* faint base tint */}
              <rect
                x="0"
                y="0"
                width="12"
                height="12"
                fill={resolvedColor}
                opacity="0.15"
              />
              {/* hash stripe */}
              <rect
                x="0"
                y="0"
                width="6"
                height="12"
                fill={resolvedColor}
                opacity="0.6"
              />
            </pattern>
          </defs>
        </svg>
        <Gauge
          width={width}
          height={height}
          value={analyzed || 0}
          cornerRadius="40%"
          startAngle={-110}
          endAngle={110}
          text={analyzed ? `${formatNumber(analyzed)}%` : '0%'}
          sx={(theme) => ({
            // Apply the hashed pattern to the arc — override MUI's default fill.
            [`& .${gaugeClasses.valueArc}`]: {
              fill: `url(#${patternId}) !important`,
              stroke: gaugeColor || '#52b202',
              strokeOpacity: 0.35,
              strokeWidth: 1,
              transition: 'all 300ms ease',
            },
            [`& .${gaugeClasses.referenceArc}`]: {
              fill: theme.palette.text.disabled || '#000000',
              opacity: 0.4,
            },
            [`& .${gaugeClasses.valueText}`]: { fontSize: 18 },
            margin: '0 auto',
          })}
        />
      </Box>
    </Tooltip>
  );
}
